import { useLocation } from 'react-router-dom';
import React from 'react';

/*
    - ihoDesignSystem
*/
import { Alert, Container, Grid } from '@iho-design-system/Loader';

/*
    - component
*/
import AgentList from '../components/user/agent/List';
import AgentToolbar from '../components/user/agent/Toolbar';
import MemberList from '../components/user/member/List';
import MemberToolbar from '../components/user/member/Toolbar';
import PassengerList from '../components/passenger/List';
import PassengerToolbar from '../components/passenger/Toolbar';
import SubOrganizationList from '../components/user/sub-organization/List';
import SubOrganizationToolbar from '../components/user/sub-organization/Toolbar';
import Tab from '../components/user/Tab';

/*
    - api
*/
import getApi from '../api/get';

const User = () => {
    const location = useLocation();

    /*
        - defaultData
    */
    const [reloadUser, setReloadUser] = React.useState('');
    const [userData, setUserData] = React.useState({
        nations: '',
        subCompanies: '',
        subOrganizations: '',
        members: '',
        agents: '',
        passengers: ''
    });

    /*
        - alertProps
    */
    const [alert, setAlert] = React.useState('');
    const [alertColor, setAlertColor] = React.useState('success');
    const [alertStatus, setAlertStatus] = React.useState('d-none');

    const alertProps = (config) => {
        setAlert(config[1]);
        setAlertColor(config[0]);
        setAlertStatus('d-flex');
        setTimeout(() => {
            setAlertStatus('d-none');
        }, 2500);
    };

    /*
        - reloadUserProps
    */
    const reloadUserProps = (reloadUserProps) => {
        setReloadUser(reloadUserProps);
    };

    /*
        - fetch nations & subCompanies on mount
    */

    /*
        - fetch data based on active tab
    */
    React.useEffect(() => {
        const fetchTabData = async () => {
            if (location.pathname === '/user/sub-organization') {
                const subOrganizations = await getApi({ ctrl: 'userAgents', method: 'GetSubOrganizations', query: '?PageIndex=1&PageSize=10' });
                setUserData(prev => ({ ...prev, subOrganizations }));
            }
            if (location.pathname === '/user/member') {
                const members = await getApi({ ctrl: 'OrganizationMember', method: 'GetMembers', query: '?PageIndex=1&PageSize=10' });
                setUserData(prev => ({ ...prev, members }));
            }
            if (location.pathname === '/user/agent') {
                const agents = await getApi({ ctrl: 'UserAgents', method: 'GetAgents', query: '?PageIndex=1&PageSize=10' });
                setUserData(prev => ({ ...prev, agents }));
            }
            if (location.pathname === '/user/passenger') {
                const [passengers, nations] = await Promise.all([
                    getApi({ ctrl: 'SiteUserPassenger', method: 'GetPassengers', query: '?PageIndex=1&PageSize=10' }),
                    getApi({ ctrl: 'common', method: 'GetAllNations', query: '' }),
                ]);
                setUserData(prev => ({ ...prev, passengers, nations }));
            }
        };
        fetchTabData();
    }, [location.pathname, reloadUser]);

    /*
        - userSearchProps
    */
    const userSearchProps = async (value) => {
        if (location.pathname === '/user/member') {
            let query = value ? `?PageIndex=1&PageSize=10&NationalCode=${value}` : '?PageIndex=1&PageSize=10';
            const members = await getApi({ ctrl: 'OrganizationMember', method: 'GetMembers', query });
            setUserData(prev => ({ ...prev, members }));
        }
        if (location.pathname === '/user/agent') {
            let query = value ? `?PageIndex=1&PageSize=10&SearchQuery=${value}` : '?PageIndex=1&PageSize=10';
            const agents = await getApi({ ctrl: 'UserAgents', method: 'GetAgents', query });
            setUserData(prev => ({ ...prev, agents }));
        }
        if (location.pathname === '/user/passenger') {
            let query = value ? `?PageIndex=1&PageSize=10&NationalCode=${value}` : '?PageIndex=1&PageSize=10';
            const passengers = await getApi({ ctrl: 'SiteUserPassenger', method: 'GetPassengers', query });
            setUserData(prev => ({ ...prev, passengers }));
        }
    };

    /*
        - paginationProps
    */
    const paginationProps = async (paginationProps) => {
        if (location.pathname === '/user/sub-organization') {
            const currentPage = userData.subOrganizations.data.PageIndex;
            const totalPages = Math.ceil(userData.subOrganizations.data.TotalItems / userData.subOrganizations.data.PageSize);
            if (currentPage + paginationProps >= 1 && currentPage + paginationProps <= totalPages) {
                const subOrganizations = await getApi({ ctrl: 'userAgents', method: 'GetSubOrganizations', query: `?PageIndex=${currentPage + paginationProps}&PageSize=10` });
                setUserData(prev => ({ ...prev, subOrganizations }));
            }
        }
        if (location.pathname === '/user/member') {
            const currentPage = userData.members.data.PageIndex;
            const totalPages = Math.ceil(userData.members.data.TotalItems / userData.members.data.PageSize);
            if (currentPage + paginationProps >= 1 && currentPage + paginationProps <= totalPages) {
                const members = await getApi({ ctrl: 'OrganizationMember', method: 'GetMembers', query: `?PageIndex=${currentPage + paginationProps}&PageSize=10` });
                setUserData(prev => ({ ...prev, members }));
            }
        }
        if (location.pathname === '/user/agent') {
            const currentPage = userData.agents.data.PageIndex;
            const totalPages = Math.ceil(userData.agents.data.TotalItems / userData.agents.data.PageSize);
            if (currentPage + paginationProps >= 1 && currentPage + paginationProps <= totalPages) {
                const agents = await getApi({ ctrl: 'UserAgents', method: 'GetAgents', query: `?PageIndex=${currentPage + paginationProps}&PageSize=10` });
                setUserData(prev => ({ ...prev, agents }));
            }
        }
        if (location.pathname === '/user/passenger') {
            const currentPage = userData.passengers.data.PageIndex;
            const totalPages = Math.ceil(userData.passengers.data.TotalItems / userData.passengers.data.PageSize);
            if (currentPage + paginationProps >= 1 && currentPage + paginationProps <= totalPages) {
                const passengers = await getApi({ ctrl: 'SiteUserPassenger', method: 'GetPassengers', query: `?PageIndex=${currentPage + paginationProps}&PageSize=10` });
                setUserData(prev => ({ ...prev, passengers }));
            }
        }
    };

    /*
        - pageTab
    */
    let pageTab = '';
    if (location.pathname === '/user/member') pageTab = 'member';
    if (location.pathname === '/user/agent') pageTab = 'agent';
    if (location.pathname === '/user/passenger') pageTab = 'passenger';
    if (location.pathname === '/user/sub-organization') pageTab = 'subOrganization';

    /*
        - componentData
    */
    const toolbarData = {
        nations: userData.nations,
        subCompanies: userData.subCompanies
    };

    const listData = {
        nations: userData.nations,
        subOrganizations: userData.subOrganizations,
        members: userData.members,
        agents: userData.agents,
        passengers: userData.passengers
    };

    return (
        <React.Fragment>
            <Container size='lg'>
                <Grid>
                    <Grid.Wrapper>
                        <Grid.Item size='col-12'>
                            <Tab />
                        </Grid.Item>
                        <Grid.Item className={`pr-0 pl-0`} size='col-12'>
                            {pageTab === 'subOrganization' && (
                                <SubOrganizationToolbar
                                    props={toolbarData}
                                    alertProps={alertProps}
                                    nationalCodeSearchProps={userSearchProps}
                                    reloadUserProps={reloadUserProps}
                                />
                            )}
                            {pageTab === 'member' && (
                                <MemberToolbar
                                    props={toolbarData}
                                    alertProps={alertProps}
                                    nationalCodeSearchProps={userSearchProps}
                                    reloadUserProps={reloadUserProps}
                                />
                            )}
                            {pageTab === 'agent' && (
                                <AgentToolbar
                                    props={toolbarData}
                                    alertProps={alertProps}
                                    multiSearchProps={userSearchProps}
                                    reloadUserProps={reloadUserProps}
                                />
                            )}
                            {pageTab === 'passenger' && (
                                <PassengerToolbar
                                    props={toolbarData}
                                    alertProps={alertProps}
                                    nationalCodeSearchProps={userSearchProps}
                                    reloadPassengerProps={reloadUserProps}
                                />
                            )}
                        </Grid.Item>
                        <Grid.Item className={`pr-0 pl-0`} size='col-12'>
                            {pageTab === 'subOrganization' && (
                                <SubOrganizationList
                                    props={listData}
                                    paginationProps={paginationProps}
                                />
                            )}
                            {pageTab === 'member' && (
                                <MemberList
                                    props={listData}
                                    paginationProps={paginationProps}
                                    alertProps={alertProps}
                                    reloadUserProps={reloadUserProps}
                                />
                            )}
                            {pageTab === 'agent' && (
                                <AgentList
                                    props={listData}
                                    paginationProps={paginationProps}
                                    alertProps={alertProps}
                                    reloadUserProps={reloadUserProps}
                                />
                            )}
                            {pageTab === 'passenger' && (
                                <PassengerList
                                    props={listData}
                                    alertProps={alertProps}
                                    paginationProps={paginationProps}
                                    reloadPassengerProps={reloadUserProps}
                                />
                            )}
                        </Grid.Item>
                    </Grid.Wrapper>
                </Grid>
            </Container>
            <Alert
                className={`alert ${alertStatus}`}
                color={alertColor}
                ms={{ bottom: '75px !important' }}
            >
                {alert}
            </Alert>
        </React.Fragment>
    );
};

export default User;
